import { ApiTags } from '@constants/common';
import { GenericApiResponse } from '@type/general';
import { Order } from '@components/table/types';
import restApi from '@services/api';
import {
  ConfirmPaymentRequestType,
  DownloadReportResponseType,
  DealsAnalyticsSummaryResponseType,
  DownloadDocumentsPollRequest,
  DownloadDocumentsPollResponse,
  DownloadDocumentsRequest,
  DownloadDocumentsResponse,
  DropCommitRequest,
  DropCommitResponse,
  GetDealAnalyticsDetailsResponse,
  Payments,
  PaymentsHistoryReq,
  PollResponse,
  UnitCertificateGenerationRequest,
  UserMaskingRequestType,
  SendEmailUnitCertificateRequest
} from './types';
import { tableHeaderMapper } from './constants';
const GetDealsApi = restApi.injectEndpoints({
  endpoints: builder => ({
    getDealAnalyticsDetails: builder.query<
      GetDealAnalyticsDetailsResponse,
      {
        offset?: number;
        limit?: number;
        order?: Order;
        sortBy?: string;
        searchTerm?: string;
        id: string;
      }
    >({
      query: payload => ({
        url: `${
          process.env.REACT_APP_BASE_API_VERSION
        }/support/deal/analytics/${payload.id}?${
          payload.searchTerm && `&search=${payload.searchTerm}`
        }&limit=${payload.limit}&offset=${payload.offset}&sort=${
          tableHeaderMapper[payload.sortBy as keyof typeof tableHeaderMapper] ??
          'name'
        }&order=${payload.order}`,
        method: 'GET'
      }),

      providesTags: [
        ApiTags.dealList,
        ApiTags.recordInvestment,
        ApiTags.dealInvite,
        ApiTags.dropCommit,
        ApiTags.activateCommit,
        ApiTags.removePreCommit
      ]
    }),
    getDealsAnalyticsSummary: builder.query<
      DealsAnalyticsSummaryResponseType,
      { dealid: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealid}/analytics/summary`,
        method: 'GET'
      }),
      providesTags: [
        ApiTags.dealList,
        ApiTags.recordInvestment,
        ApiTags.dropCommit,
        ApiTags.activateCommit,
        ApiTags.dealInvite,
        ApiTags.removePreCommit
      ],
      transformResponse: (response: DealsAnalyticsSummaryResponseType) =>
        response
    }),
    getAnalyticsReport: builder.query<
      DownloadReportResponseType,
      { dealid: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealid}/analytics/report`,
        method: 'GET'
      }),
      transformResponse: (response: DownloadReportResponseType) => response
    }),
    generateReportPoll: builder.query<
      GenericApiResponse<PollResponse>,
      { jobId: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/generate-report-poll/${payload.jobId}`,
        method: 'GET'
      }),
      transformResponse: (response: GenericApiResponse<PollResponse>) =>
        response
    }),
    confirmPayment: builder.mutation<
      GenericApiResponse<string>,
      ConfirmPaymentRequestType
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/utils/payment/manual/confirm`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: [ApiTags.recordInvestment],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    GenerateUnitCertificate: builder.mutation<
      GenericApiResponse<string>,
      UnitCertificateGenerationRequest
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/user/${payload.userId}/deal/${payload.dealId}/unit-certificate/generate`,
        method: 'POST'
      })
    }),
    sendEmailAfterUnitCertificateGeneration: builder.mutation<
      GenericApiResponse<string>,
      SendEmailUnitCertificateRequest
    >({
      query: payload => ({
        // eslint-disable-next-line max-len
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/users/unit-certificate/bulk-send-emails`,
        method: 'POST',
        body: payload
      })
    }),
    getPaymentsHistory: builder.query<
      GenericApiResponse<Payments[]>,
      PaymentsHistoryReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/user/${payload.userId}/deal/${payload.dealId}/payments`,
        method: 'GET'
      }),
      providesTags: [ApiTags.recordInvestment],
      transformResponse: (response: GenericApiResponse<Payments[]>) => response
    }),
    downloadDocuments: builder.query<
      DownloadDocumentsResponse,
      DownloadDocumentsRequest
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealId}/download-documents`,
        method: 'GET'
      }),
      transformResponse: (response: DownloadDocumentsResponse) => response
    }),
    polldownloadedDocuments: builder.query<
      DownloadDocumentsPollResponse,
      DownloadDocumentsPollRequest
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/download-documents-poll/${payload.jobId}`,
        method: 'GET'
      }),
      transformResponse: (response: DownloadDocumentsPollResponse) => response
    }),
    dropCommit: builder.mutation<DropCommitResponse, DropCommitRequest>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealId}/user/${payload.userId}/drop-commit`,
        method: 'PUT'
      }),
      invalidatesTags: [ApiTags.dropCommit],
      transformResponse: (response: DropCommitResponse) => response
    }),
    activateCommit: builder.mutation<DropCommitResponse, DropCommitRequest>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.dealId}/user/${payload.userId}/activate-commit`,
        method: 'PUT'
      }),
      invalidatesTags: [ApiTags.activateCommit],
      transformResponse: (response: DropCommitResponse) => response
    }),
    getDownloadDealInviteesJobId: builder.mutation<
      GenericApiResponse<{
        job_id: string;
      }>,
      { dealId: string }
    >({
      query: ({ dealId }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/invite/status/${dealId}/export`,
        method: 'POST',
        body: {
          format: 'excel'
        }
      }),
      transformResponse: (
        response: GenericApiResponse<{
          job_id: string;
        }>
      ) => response
    }),
    getDealInviteesDowndloadUrl: builder.query<
      GenericApiResponse<{
        signed_url: string;
        status: string;
      }>,
      {
        jobId: string;
        dealId: string;
      }
    >({
      query: ({ dealId, jobId }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/invite/status/${dealId}/export/${jobId}`,
        method: 'GET'
      }),
      transformResponse: (
        response: GenericApiResponse<{
          signed_url: string;
          status: string;
        }>
      ) => response
    }),

    //Delete Pre-Commit amount
    deletePreCommit: builder.mutation<
      GenericApiResponse<string>,
      { deal_id: string; user_id: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/moneyball/precommit/${payload.deal_id}/${payload.user_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.removePreCommit]
    }),

    maskUser: builder.mutation<
      GenericApiResponse<string>,
      UserMaskingRequestType
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/masked-users`,
        method: 'POST',
        body: payload.body
      }),
      invalidatesTags: [ApiTags.dealList]
    }),

    unmaskUser: builder.mutation<
      GenericApiResponse<string>,
      UserMaskingRequestType
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/deal/${payload.deal_id}/masked-users`,
        method: 'DELETE',
        body: payload.body
      }),
      invalidatesTags: [ApiTags.dealList]
    })
  })
});

export const {
  useLazyGetDealAnalyticsDetailsQuery,
  useLazyGetAnalyticsReportQuery,
  useLazyGenerateReportPollQuery,
  useGenerateReportPollQuery,
  useConfirmPaymentMutation,
  useGetDealsAnalyticsSummaryQuery,
  useGenerateUnitCertificateMutation,
  useGetPaymentsHistoryQuery,
  useLazyDownloadDocumentsQuery,
  useLazyPolldownloadedDocumentsQuery,
  useDropCommitMutation,
  useActivateCommitMutation,
  useGetDownloadDealInviteesJobIdMutation,
  useLazyGetDealInviteesDowndloadUrlQuery,
  useDeletePreCommitMutation,
  useSendEmailAfterUnitCertificateGenerationMutation,
  useMaskUserMutation,
  useUnmaskUserMutation
} = GetDealsApi;
